/**
 * Attribution capture + cart stamping — a faithful port of littlebird-website's
 * lib/analytics/campaign.ts and hooks/useCart.ts contract:
 *
 * - Same localStorage keys (attribution_campaign / attribution_click_ids) so a visitor
 *   migrating from the headless site keeps their stored attribution.
 * - Last-touch semantics: any utm_* param atomically replaces the whole stored campaign;
 *   click ids are per-key last-touch (an unrelated touch never evicts a stored gclid).
 * - Google click-id family (gclid/gbraid/wbraid) is mutually exclusive: a fresh Google
 *   touch evicts the stored family; precedence gclid > gbraid > wbraid on one URL.
 * - fbclid is stored pre-formatted as Meta's `fbc` shape fb.1.{captureMs}.{fbclid};
 *   fbp is read from Meta's _fbp cookie and refreshed on every capture.
 * - TTLs matched to conversion windows: campaign 90d, fbc 7d, everything else 90d.
 *   Expired values drop at read; survivors keep their original capture time.
 * - Values sanitized at write AND read: 1-255 non-control characters.
 * - Consent gates at STAMP time (where values leave the device), not capture, via
 *   Shopify's Customer Privacy API; no consent framework installed = allowed.
 * - `_lb_anonymous_id` uses/pre-seeds Segment's ajs_anonymous_id so ids stay continuous
 *   whether or not analytics.js has loaded yet. `_lb_cart_id` is minted per cart, not
 *   the Shopify cart token (attributes are what survive into Order.customAttributes).
 */
window.LBAttribution = (() => {
  const CAMPAIGN_KEY = 'attribution_campaign';
  const CLICK_IDS_KEY = 'attribution_click_ids';
  const DAY_MS = 86400000;
  const CAMPAIGN_TTL_MS = 90 * DAY_MS;
  const CLICK_ID_TTL_MS = { fbc: 7 * DAY_MS, fbp: 90 * DAY_MS, gbraid: 90 * DAY_MS, gclid: 90 * DAY_MS, msclkid: 90 * DAY_MS, ttclid: 90 * DAY_MS, wbraid: 90 * DAY_MS };
  const UTM_TO_CAMPAIGN = { utm_campaign: 'name', utm_content: 'content', utm_medium: 'medium', utm_source: 'source', utm_term: 'term' };
  const CLICK_ID_PARAMS = ['gbraid', 'gclid', 'msclkid', 'ttclid', 'wbraid'];
  const GOOGLE_CLICK_IDS = ['gclid', 'gbraid', 'wbraid'];
  const CLICK_ID_FIELDS = CLICK_ID_PARAMS.concat(['fbc', 'fbp']);
  // Cart attribute key per attribution field — the pixel and order webhook read these names.
  const ATTRIBUTE_KEYS = {
    content: '_lb_utm_content', fbc: '_lb_fbc', fbp: '_lb_fbp', gbraid: '_lb_gbraid',
    gclid: '_lb_gclid', medium: '_lb_utm_medium', msclkid: '_lb_msclkid', name: '_lb_utm_campaign',
    source: '_lb_utm_source', term: '_lb_utm_term', ttclid: '_lb_ttclid', wbraid: '_lb_wbraid',
  };
  const CAMPAIGN_ATTRIBUTE_KEY_SET = new Set(Object.values(ATTRIBUTE_KEYS));
  const ANONYMOUS_ID_KEY = '_lb_anonymous_id';
  const CART_ID_KEY = '_lb_cart_id';

  const VALUE_PATTERN = /^[^\x00-\x1f\x7f-\x9f]{1,255}$/;
  const sanitize = (raw) => {
    const v = (raw || '').trim();
    return v && VALUE_PATTERN.test(v) ? v : undefined;
  };
  const readJson = (key) => { try { const r = localStorage.getItem(key); return r ? JSON.parse(r) : undefined; } catch { return undefined; } };
  const write = (key, record) => { try { localStorage.setItem(key, JSON.stringify(record)); } catch { /* private mode: URL-only attribution this visit */ } };
  const isRecord = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);

  const readClickIdEntries = () => {
    const parsed = readJson(CLICK_IDS_KEY);
    if (!isRecord(parsed)) return {};
    const now = Date.now();
    const out = {};
    for (const field of CLICK_ID_FIELDS) {
      const e = parsed[field];
      if (!isRecord(e)) continue;
      const value = typeof e.value === 'string' ? sanitize(e.value) : undefined;
      if (typeof e.capturedAt === 'number' && value && now - e.capturedAt <= CLICK_ID_TTL_MS[field]) {
        out[field] = { capturedAt: e.capturedAt, value };
      }
    }
    return out;
  };

  const readFbpCookie = () => {
    const m = document.cookie.match(/(?:^|;\s*)_fbp=([^;]*)/);
    return m ? sanitize(m[1]) : undefined;
  };

  const capture = () => {
    const params = new URLSearchParams(location.search);
    // Campaign: atomic replace when any utm_* present.
    const fields = {};
    for (const [param, field] of Object.entries(UTM_TO_CAMPAIGN)) {
      const v = sanitize(params.get(param));
      if (v) fields[field] = v;
    }
    if (Object.keys(fields).length > 0) write(CAMPAIGN_KEY, { capturedAt: Date.now(), fields });
    // Click ids: per-key last-touch; Google family evicts as a unit.
    const now = Date.now();
    const fresh = {};
    for (const p of CLICK_ID_PARAMS) {
      const v = sanitize(params.get(p));
      if (v) fresh[p] = { capturedAt: now, value: v };
    }
    const fbclid = sanitize(params.get('fbclid'));
    if (fbclid) fresh.fbc = { capturedAt: now, value: 'fb.1.' + now + '.' + fbclid };
    const fbp = readFbpCookie();
    if (fbp) fresh.fbp = { capturedAt: now, value: fbp };
    if (Object.keys(fresh).length === 0) return;
    const stored = readClickIdEntries();
    const googleWinner = GOOGLE_CLICK_IDS.filter((k) => fresh[k])[0];
    if (googleWinner) {
      for (const k of GOOGLE_CLICK_IDS) {
        delete stored[k];
        if (k !== googleWinner) delete fresh[k];
      }
    }
    write(CLICK_IDS_KEY, Object.assign(stored, fresh));
  };

  const getStoredCampaign = () => {
    const parsed = readJson(CAMPAIGN_KEY);
    if (!isRecord(parsed) || typeof parsed.capturedAt !== 'number' || Date.now() - parsed.capturedAt > CAMPAIGN_TTL_MS) return undefined;
    if (!isRecord(parsed.fields)) return undefined;
    const out = {};
    for (const f of Object.values(UTM_TO_CAMPAIGN)) {
      const v = typeof parsed.fields[f] === 'string' ? sanitize(parsed.fields[f]) : undefined;
      if (v) out[f] = v;
    }
    return Object.keys(out).length ? out : undefined;
  };

  const getStoredAttribution = () => {
    const merged = Object.assign({}, getStoredCampaign());
    for (const [field, entry] of Object.entries(readClickIdEntries())) merged[field] = entry.value;
    return Object.keys(merged).length ? merged : undefined;
  };

  /* ---- Consent (Shopify Customer Privacy API; site parity: gate at stamp, not capture) ---- */
  const privacyApi = () => (window.Shopify && window.Shopify.customerPrivacy) || undefined;
  const hasMarketingConsent = () => {
    const cp = privacyApi();
    if (!cp || typeof cp.marketingAllowed !== 'function') return true; // no consent framework configured
    return cp.marketingAllowed() !== false;
  };
  const hasDeniedMarketingConsent = () => {
    const cp = privacyApi();
    return !!cp && typeof cp.marketingAllowed === 'function' && cp.marketingAllowed() === false;
  };

  /* ---- Ids ---- */
  const uuid = () => (crypto.randomUUID ? crypto.randomUUID() : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => { const r = (Math.random() * 16) | 0; return (c === 'x' ? r : (r & 3) | 8).toString(16); }));
  /** Segment-compatible anonymous id: reuse ajs_anonymous_id if analytics.js set it; pre-seed it otherwise so analytics.js adopts ours. */
  const getAnonymousId = () => {
    try {
      if (window.analytics && window.analytics.user) {
        const id = window.analytics.user().anonymousId();
        if (id) return id;
      }
      const raw = localStorage.getItem('ajs_anonymous_id');
      if (raw) return raw.replace(/^"|"$/g, '');
      const id = uuid();
      localStorage.setItem('ajs_anonymous_id', JSON.stringify(id));
      return id;
    } catch { return undefined; }
  };

  /* ---- Cart stamping (Ajax API: /cart/update.js merges attributes; '' deletes a key) ---- */
  const buildCampaignAttributes = () => {
    if (!hasMarketingConsent()) return {};
    const attribution = getStoredAttribution();
    if (!attribution) return {};
    const attrs = {};
    for (const [field, key] of Object.entries(ATTRIBUTE_KEYS)) {
      if (attribution[field]) attrs[key] = attribution[field];
    }
    return attrs;
  };

  /**
   * Cart-view / post-add sync, mirroring resolveSyncedAttributes: a consented stored campaign
   * replaces the cart's set; with nothing stored, strip only once the consent decision is made;
   * backfill anonymous id and cart id when missing. Returns the merge-payload or null.
   */
  const resolveSync = (currentAttributes) => {
    const existing = {};
    for (const a of currentAttributes || []) existing[a.key] = a.value;
    const desired = buildCampaignAttributes();
    const currentCampaignKeys = Object.keys(existing).filter((k) => CAMPAIGN_ATTRIBUTE_KEY_SET.has(k));
    const payload = {};
    if (Object.keys(desired).length > 0) {
      for (const k of currentCampaignKeys) if (!(k in desired)) payload[k] = '';
      for (const [k, v] of Object.entries(desired)) if (existing[k] !== v) payload[k] = v;
    } else if (currentCampaignKeys.length > 0 && (hasMarketingConsent() ? true : hasDeniedMarketingConsent())) {
      // Consent decided and nothing stored (expired or revoked) → strip stale campaign.
      for (const k of currentCampaignKeys) payload[k] = '';
    }
    const anonymousId = getAnonymousId();
    if (anonymousId && !existing[ANONYMOUS_ID_KEY]) payload[ANONYMOUS_ID_KEY] = anonymousId;
    if (!existing[CART_ID_KEY]) payload[CART_ID_KEY] = uuid();
    return Object.keys(payload).length ? payload : null;
  };

  /** Fetches the cart and reconciles `_lb_*` attributes. Fire-and-forget; must never block the purchase. */
  const syncCartAttributes = async () => {
    try {
      const cart = await (await fetch('/cart.js')).json();
      if (!cart || cart.item_count === 0) return; // no cart journey yet — nothing to stamp
      const attrs = Object.entries(cart.attributes || {}).map(([key, value]) => ({ key, value }));
      const payload = resolveSync(attrs);
      if (!payload) return;
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: payload }),
      });
    } catch { /* attribution is best-effort */ }
  };

  capture();
  // Refresh stamps on cart-relevant pages (mirrors the site's cart-view sync).
  if (/^\/cart/.test(location.pathname)) syncCartAttributes();

  return { capture, getStoredAttribution, getStoredCampaign, getAnonymousId, hasMarketingConsent, syncCartAttributes };
})();
